import React, { useRef, useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { get } from 'lodash-es';
import classnames from 'classnames';
import { Modal, Container, Row, Col, Button, Input } from 'reactstrap';
import { Storage } from 'site-modules/shared/utils/storage';
import { getPlural } from 'client/utils/plural';
import { EDMUNDS_LOGO_NO_TEXT } from 'client/images/inline-svgs/edmunds-logo';
import { TrackingConstant } from 'client/tracking/constant';
import { EventToolbox } from 'client/utils/event-toolbox';

import './satisfaction-widget.scss';

const IS_SATISFACTION_WIDGET_CLOSED_STORAGE_KEY = 'isSatisfactionWidgetClosed';
export const CONFIG = {
  questions: [
    { question: 'Is this page helpful to you in finding a great deal?', trackingName: 'user-sat-survey_displayed' },
    {
      question: 'Does Edmunds offer unique pricing data?',
      trackingName: 'user-sat-survey_displayed',
    },
    { question: 'Thank you for your feedback!', trackingName: 'user-sat-survey_completed' },
  ],
  questionsWithFeedback: [
    { question: 'Is this page helpful to you in finding a great deal?', trackingName: 'user-sat-survey_displayed' },
    {
      question: 'Does Edmunds offer unique pricing data?',
      trackingName: 'user-sat-survey_displayed',
    },
    { question: "Any feedback you'd like to share for our team?", trackingName: 'user-sat-survey_displayed' },
    { question: 'Thank you for your feedback!', trackingName: 'user-sat-survey_completed' },
  ],
};
export const TRACKING_TYPE = {
  DISPLAY: 'display',
  ANSWER: 'answer',
};

export function SatisfactionWidget({
  storageKey = IS_SATISFACTION_WIDGET_CLOSED_STORAGE_KEY,
  isFeedbackEnabled = false,
}) {
  const storageRef = useRef(new Storage('localStorage'));
  const [isOpen, setIsOpen] = useState(false);
  const [questionIndex, setQuestionIndex] = useState(0);
  const [count, setCount] = useState(300);
  const [feedback, setFeedback] = useState('');

  const questions = isFeedbackEnabled ? CONFIG.questionsWithFeedback : CONFIG.questions;

  const openSurvey = () => {
    setIsOpen(true);
  };

  const setSurveyCompleted = () => {
    storageRef.current.set(storageKey, true);
    setIsOpen(false);
  };

  const getCurrentQuestion = () => questions[questionIndex];

  const trackQuestion = useCallback((trackingType, selectedAnswer) => {
    const currentQuestion = getCurrentQuestion();
    const trackingName = get(currentQuestion, 'trackingName');
    const trackingValue = get(currentQuestion, 'question');

    let eventData;

    if (trackingType === TRACKING_TYPE.DISPLAY) {
      eventData = {
        action_name: TrackingConstant.ACTION_SHOW_CONTENT,
        subaction_name: trackingName,
        action_category: TrackingConstant.SYSTEM_ACTION_CATEGORY,
        action_cause: TrackingConstant.ACTION_CAUSE_SCROLL,
        value: `survey_question=${trackingValue}`,
      };
    }
    if (trackingType === TRACKING_TYPE.ANSWER) {
      eventData = {
        action_cause: TrackingConstant.ACTION_CAUSE_BUTTON_CLICK,
        value: `survey_question=${trackingValue},survey_answer=${selectedAnswer}`,
      };
    }

    EventToolbox.fireTrackAction({
      event_type: TrackingConstant.EVENT_TYPE_ACTION_COMPLETED,
      event_data: {
        action_name: TrackingConstant.ACTION_VIEW_CONTENT,
        subaction_name: TrackingConstant.USER_SAT_SURVEY_ANSWER,
        action_category: TrackingConstant.USER_ACTION_CATEGORY,
        ...eventData,
      },
    });
  });

  const submitAnswer = e => {
    e.preventDefault();
    let answer = get(e, 'currentTarget.dataset.value', '');

    if (questionIndex === 2 && isFeedbackEnabled) {
      answer = document.querySelector('#feedback').value;
    }

    const nextQuestionIndex = questionIndex + 1;
    trackQuestion(TRACKING_TYPE.ANSWER, answer);
    setQuestionIndex(nextQuestionIndex);
  };

  const handleChange = e => {
    const value = e.target.value;

    setFeedback(value);
    setCount(300 - value.length);
  };

  useEffect(
    () => {
      if (!storageRef.current.get(storageKey)) {
        openSurvey();
      }
    },
    // `useEffect` will work as `componentDidMount` if dependencies array is empty
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  useEffect(
    () => {
      trackQuestion(TRACKING_TYPE.DISPLAY);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [questionIndex]
  );

  const currentQuestion = getCurrentQuestion();
  const hideAnswers = questionIndex > 1;
  const showFeedbackScreen = questionIndex === 2 && isFeedbackEnabled;

  return (
    <Modal
      modalClassName="d-flex"
      className="satisfaction-widget m-auto"
      contentClassName="pos-r px-2_5 py-2_5"
      isOpen={isOpen}
      labelledBy="satisfaction-widget-title"
    >
      <div id="satisfaction-widget-title" className="visually-hidden" role="heading" aria-level={2}>
        Edmunds feedback survey
      </div>
      <Button
        color="link"
        size="lg"
        className="close-btn p-0_75 text-decoration-none text-gray-dark"
        aria-label="Close"
        data-testid="close-btn"
        onClick={setSurveyCompleted}
      >
        <i className="icon-cross2 size-16" aria-hidden />
      </Button>
      <div className="content-copy small ps-2_5 pt-1_5">
        <EDMUNDS_LOGO_NO_TEXT width={15} fillColor="#0069bf" /> Edmunds feedback
      </div>
      {(!hideAnswers || showFeedbackScreen) && (
        <div className="heading-3 fw-normal mt-2 mb-1">
          {showFeedbackScreen ? `Any feedback you'd like to share for our team?` : 'Can we ask you something?'}
        </div>
      )}

      <Container
        className={classnames('hide-wrapper px-0 w-100', { show: currentQuestion })}
        aria-hidden={!currentQuestion}
      >
        <Row>
          <Col
            xs={12}
            className={classnames('question-label text-nowrap pos-r', { 'no-feedback': !showFeedbackScreen })}
          >
            {questions.map(({ question }, index) => (
              <div
                className={classnames('question-text d-inline-block w-100 h-100 align-middle', {
                  'not-answered': index > questionIndex,
                  answered: index < questionIndex,
                })}
                key={`${question}`}
                style={{ left: `${index * -100}%` }}
              >
                {index === 2 ? (
                  <>
                    <Input
                      type="textarea"
                      name="feedback"
                      id="feedback"
                      maxLength={300}
                      rows={6}
                      value={feedback}
                      placeholder="Please tell us your thoughts"
                      onChange={handleChange}
                      data-testid="feedback"
                    />
                    <div className={classnames('small mt-0_25', { 'text-danger': count <= 10 })}>
                      {count} {getPlural('character', count)} remaining
                    </div>
                    <Button
                      className="px-1 py-0_5"
                      color="outline-primary-b mt-1 size-16"
                      onClick={submitAnswer}
                      aria-label={`${question} Submit`}
                      data-testid="submit"
                    >
                      Submit
                    </Button>
                  </>
                ) : (
                  <div className={classnames('fw-bold mb-1_5', { 'text-center mt-3': hideAnswers })}>{question}</div>
                )}

                {!hideAnswers && (
                  <div className="text-center">
                    <Button
                      className="text-capitalize answer-btn px-1 py-0_75 me-2"
                      onClick={submitAnswer}
                      data-value="Thumbs Up"
                      aria-label={`${question} Thumbs Up`}
                      data-testid={`${index}-button-yes`}
                      outline
                    >
                      <i className="icon-thumbs-up2 text-primary-darker size-24 me-0_75" aria-hidden />
                      <span className="heading-4 fw-normal text-primary-darker">Yes</span>
                    </Button>
                    <Button
                      className="text-capitalize answer-btn px-1 py-0_75 pos-r"
                      onClick={submitAnswer}
                      data-value="Thumbs Down"
                      aria-label={`${question} Thumbs Down`}
                      data-testid={`${index}-button-no`}
                      outline
                    >
                      <i className="icon icon-thumbs-down22 text-primary-darker size-24 me-0_75" aria-hidden />
                      <span className="heading-4 fw-normal text-primary-darker">No</span>
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </Col>
        </Row>
      </Container>
    </Modal>
  );
}

SatisfactionWidget.propTypes = {
  storageKey: PropTypes.string,
  isFeedbackEnabled: PropTypes.bool,
};
